import { useEffect, type MutableRefObject } from 'react';
import { editorViewCtx, parserCtx, serializerCtx } from '@milkdown/kit/core';
import { useInstance } from '@milkdown/react';

import type { ReviewSuggestionBulkActions } from '../../types';
import { normalizeMarkdown } from '../../useEditorReady';
import type { ParseProposedMd } from './decorations';
import { createReviewSuggestionBulkActions, parseProposedMd } from './plugin';

/** Bound RAF retries while waiting for Milkdown ctx (e.g. `parserCtx`). ~10s at 60fps. */
const BULK_ACTIONS_RAF_RETRY_CAP = 600;

/**
 * Keeps `reviewSuggestionBulkActionsRef` in sync with the live editor: clears
 * while loading, then populates via `createReviewSuggestionBulkActions` once
 * `editorViewCtx` / `serializerCtx` are registered (RAF retry on "not found").
 *
 * Must run in the same React subtree as `Milkdown` / `useEditor` (same requirement as `useInstance`).
 */
export function useReviewSuggestionBulkActionsRef(
  reviewSuggestionBulkActionsRef: MutableRefObject<ReviewSuggestionBulkActions | null> | undefined,
): void {
  const [loadingInstance, getInstance] = useInstance();

  useEffect(() => {
    if (!reviewSuggestionBulkActionsRef) return;

    if (loadingInstance) {
      reviewSuggestionBulkActionsRef.current = null;

      return;
    }

    let cancelled = false;
    let actions: ReviewSuggestionBulkActions | null = null;

    const populate = () => {
      if (cancelled) return true;
      const editor = getInstance();
      if (!editor) return false;
      try {
        editor.action((ctx) => {
          const view = ctx.get(editorViewCtx);
          const serializer = ctx.get(serializerCtx);
          const parser = ctx.get(parserCtx);
          const parseMarkdown: ParseProposedMd = (md) => parseProposedMd(parser, md);
          actions = createReviewSuggestionBulkActions(view, () => normalizeMarkdown(serializer(view.state.doc)), parseMarkdown);
          if (!cancelled) {
            reviewSuggestionBulkActionsRef.current = actions;
          }
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        if (!message.includes('not found')) throw error;

        return false;
      }

      return actions !== null;
    };

    let rafId: number | null = null;
    let rafAttempts = 0;
    const tryPopulate = () => {
      if (populate()) return;
      rafAttempts += 1;
      if (rafAttempts >= BULK_ACTIONS_RAF_RETRY_CAP) {
        console.warn(`useReviewSuggestionBulkActionsRef: stopped retrying ctx registration after ${BULK_ACTIONS_RAF_RETRY_CAP} frames.`);

        return;
      }
      // ctx slot not yet registered — retry next frame until we succeed or unmount.
      rafId = requestAnimationFrame(tryPopulate);
    };
    tryPopulate();

    return () => {
      cancelled = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (reviewSuggestionBulkActionsRef.current === actions) {
        reviewSuggestionBulkActionsRef.current = null;
      }
    };
  }, [getInstance, loadingInstance, reviewSuggestionBulkActionsRef]);
}
